import { 
    PathCategories,
    PathCategoryResponse,
    PathGpsCoordinates,
    PathResponse,
    Paths,
    PoiCategories,
    PoiCategoryResponse,
    EventCategoryResponse,
    PoiResponse,
    Pois,
    SnowmobilePathResponse,
    SnowmobilePaths,
    SnowmobileSubPathResponse,
    SnowmobileSubPaths,
    AoiResponse,
    Aois,
    AoiCategoryResponse,
    AoiCategories,
    AoiGpsCoordinates
} from "../interfaces";

/**
 * Converts the PathResponse array to a useable hashmap of paths
 */
export const toPaths = (pathsOrResult: PathResponse[] | { rows: PathResponse[]; count: number }): Paths => {
    const paths = Array.isArray(pathsOrResult) ? pathsOrResult : pathsOrResult.rows;
    const reduced = paths.reduce((acc, path) => ({
        ...acc,
        [`${path.id}`]: {
            ...path,
            id: `${path.id}`,
            pathCategoryId: `${path.pathCategoryId}`,
            tags: path.tags.map(({id, name}) => ({id, name})),
            areas: path.areas.map(({id, name}) => ({id, name})),
            pois: path.pois.map(({id, name}) => ({id, name})),
            ...(path.medias?.length ? {imageUrls: path.medias.map(({fullUrl}) => fullUrl)} : {}),
        },
    }), {});

    return reduced as Paths;
};

/**
 * Converts the PathResponse array to a useable hashmap of GPS coordinates
 */
export const toPathGpsCoordinates = (pathsOrResult: PathResponse[] | { rows: PathResponse[]; count: number }): PathGpsCoordinates => {
    const paths = Array.isArray(pathsOrResult) ? pathsOrResult : pathsOrResult.rows;
    const reduced = paths.reduce((acc, path) => ({
        ...acc,
        [`${path.id}`]: path.pathGpsCoordinates.map((coordinate: any) => ({
            ...coordinate,
        })),
    }), {});

    return reduced as PathGpsCoordinates;
};

/**
 * Converts both PathCategoryResponse array to a useable hashmap of path categories
 */
export const toPathCategories = (categories: PathCategoryResponse[]): PathCategories => {
    const reduced = categories.reduce((acc, category) => ({
        ...acc,
        [`${category.id}`]: {
            ...category,
            id: `${category.id}`,
            imageUrl: category.media?.fullUrl,
        },
    }), {});

    return reduced as PathCategories;
};

/**
 * Converts the PoiResponse array to a useable hashmap of pois formatted for the map.
 * Events get an `event-` prefixed key since their ids overlap with POI ids.
 */
export const getEventPoiKey = (id: number | string): string => `event-${id}`;

/**
 * Event category ids overlap with POI category ids, so on the map they get their own key namespace.
 */
export const getEventCategoryKey = (id: number | string): string => `event-${id}`;

export const toPois = (poisOrResult: PoiResponse[] | { rows: PoiResponse[]; count: number }): Pois => {
    const pois = Array.isArray(poisOrResult) ? poisOrResult : poisOrResult.rows;
    const reduced = pois.reduce((acc, poi) => {
        const isEvent = poi.eventCategoryId != null;
        const id = isEvent ? getEventPoiKey(poi.id) : `${poi.id}`;
        return {
            ...acc,
            [id]: {
                ...poi,
                id,
                poiCategoryId: isEvent ? getEventCategoryKey(poi.eventCategoryId!) : `${poi.pointOfInterestCategoryId}`,
                eventCategoryId: isEvent ? `${poi.eventCategoryId}` : undefined,
                sourceType: isEvent ? 'event' : 'poi',
                sourceId: isEvent ? `${poi.id}` : undefined,
                linkedPoiId: isEvent && poi.pointOfInterestId != null ? `${poi.pointOfInterestId}` : undefined,
                pointOfInterestCategoryId: undefined,
                areas: (poi.areas ?? []).map(({id, name}) => ({id, name})),
                ...(poi.medias?.length ? {imageUrls: poi.medias.map(({fullUrl}) => fullUrl)} : {}),
            },
        };
    }, {});

    return reduced as Pois;
};

/**
 * Converts EventCategoryResponse array to POI categories keyed with getEventCategoryKey
 */
export const toEventPoiCategories = (categories: EventCategoryResponse[]): PoiCategories => {
    const reduced = categories.reduce((acc, category) => ({
        ...acc,
        [getEventCategoryKey(category.id)]: {
            ...category,
            id: getEventCategoryKey(category.id),
            imageUrl: category.media?.fullUrl,
        },
    }), {});

    return reduced as PoiCategories;
};

/**
 * Converts both PathCategoryResponse array to a useable hashmap of path categories
 */
export const toPoiCategories = (categories: PoiCategoryResponse[]): PoiCategories => {
    const reduced = categories.reduce((acc, category) => ({
        ...acc,
        [`${category.id}`]: {
            ...category,
            id: `${category.id}`,
            imageUrl: category.media?.fullUrl,
        },
    }), {});

    return reduced as PoiCategories;
};

/**
 * Converts the SnowmobilePathResponse array to a useable hashmap of snowmobile paths
 */
export const toSnowmobilePaths = (paths: SnowmobilePathResponse[]): SnowmobilePaths => {
    const reduced = paths.reduce((acc, path) => ({
        ...acc,
        [`${path.id}`]: {
            id: `${path.id}`,
            subPaths: path.subPaths.map(subPath => `${subPath.id}`),
        },
    }), {});

    return reduced as SnowmobilePaths;
};

/**
 * Converts the SnowmobileSubPathResponse array to a useable hashmap of snowmobile subpaths
 */
export const toSnowmobileSubPaths = (paths: SnowmobileSubPathResponse[]): SnowmobileSubPaths => {
    const reduced = paths.reduce((acc, subPath) => ({
        ...acc,
        [`${subPath.id}`]: {
            ...subPath,
            id: `${subPath.id}`,
            status: subPath.status,
            subPathGPSCoordinates: subPath.subPathGPSCoordinates.map(gpsCoordinates => ({
                longitude: gpsCoordinates.longitude,
                latitude: gpsCoordinates.latitude,
            })),
            areas: subPath.areas.map(({id, name}) => ({id, name})),
        },
    }), {});

    return reduced as SnowmobileSubPaths;
};

/**
 * Converts the numeric length to a presentable kilometer string
 */
export const toKilometerString = (length: number | null | undefined): string => {
    if (length == null) return '— km';
    return `${length.toFixed(1).replace('.', ',')} km`;
};

/**
 * Converts the AoiResponse array to a useable hashmap of aois formatted for the map
 */
export const toAois = (aoisOrResult: AoiResponse[] | { rows: AoiResponse[]; count: number }): Aois => {
    const aois = Array.isArray(aoisOrResult) ? aoisOrResult : aoisOrResult.rows;
    const reduced = aois.reduce((acc, aoi) => ({
        ...acc,
        [`${aoi.id}`]: {
            ...aoi,
            id: `${aoi.id}`,
            aoiCategoryId: `${aoi.areaOfInterestCategoryId}`,
            areaOfInterestCategoryId: undefined,
            areas: aoi?.areas?.map(({id, name}) => ({id, name})),
            ...(aoi.medias?.length ? {imageUrls: aoi.medias.map(({fullUrl}) => fullUrl)} : {}),
        },
    }), {});

    return reduced as Aois;
};

/**
 * Converts both AoiCategoryResponse array to a useable hashmap of path categories
 */
export const toAoiCategories = (categories: AoiCategoryResponse[]): AoiCategories => {
    const reduced = categories.reduce((acc, category) => ({
        ...acc,
        [`${category.id}`]: {
            ...category,
            id: `${category.id}`,
            imageUrl: category.media?.fullUrl,
        },
    }), {});

    return reduced as AoiCategories;
};

/**
 * Converts the AoiResponse array to a useable hashmap of GPS coordinates
 */
export const toAoiGpsCoordinates = (aoisOrResult: AoiResponse[] | { rows: AoiResponse[]; count: number }): AoiGpsCoordinates => {
    const aois = Array.isArray(aoisOrResult) ? aoisOrResult : aoisOrResult.rows;
    const reduced = aois.reduce((acc, aoi) => ({
        ...acc,
        [`${aoi.id}`]: aoi.aoiGpsCoordinates.map((coordinate: any) => ({
            ...coordinate,
        })),
    }), {});

    return reduced as AoiGpsCoordinates;
};
